// src/encounter/lib/l360-care-model-inputs.ts
// L360: the five care-model INPUT templates, read into the Workflows event each one drives. Pure: no network, no React.
// Same design as l360-history-review.ts and l360-lifestyle-intake.ts. The sender is services/use-l360-care-model-input-event.ts.
//
// A template is found by the code of its root group in longevity360-template, never by a GUID:
//   T12-M5 -> metabolic.plan_proposed    T12-F5 -> functional.plan_proposed    T12-P5 -> lifestyle.plan_proposed
//   CLEARANCE-F1 -> functional.clearance_recorded    CPET-F2 -> cpet.review_recorded
// A field is the item coded longevity360-field `<CODE>.<field_id>`. A choice answer arrives as valueCoding.code
// `l360-<code lowercased>.<field_id>.<slug of display>`; the option is the part after the last '.'.
//
// `careModelInputEvent` gives null (post nothing) when: the template is not an input template; a required field is open;
// a plan proposal has no care plan (the caller passes the patient's ONE L360 plan, or null); or the CPET report is
// illustrative (an illustrative value never counts toward the care model).
//
// The revision is FNV-1a (hex) over the answer STATES that matter, plus the reviewer id, never free text, so typing in a
// text field does not post again on each autosave:
//   plan proposals: the care plan id, which of the six fields are answered, the owner_role code, the start and review dates;
//   clearance: the three decisions, the cpet protocol code, whether the hold reason is answered;
//   CPET review: test_date, effort_valid, completed_safely, protocol, report_type, whether vo2_max is answered, the Report
//   text. Never the saved row id of the reportRef fallback: the same answers in a new row of the answer set are the same key.
import type { FhirQuestionnaire, FhirQuestionnaireItem } from '@/types/template-studio-api';
import type { QRAnswerWire, QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { answerSetIdOf } from './answer-set';
import { L360_TEMPLATE_SYSTEM, collectItems, hasValue, indexResponse } from './l360-history-review';

const FIELD_SYSTEM = 'https://fhir.scrips.com/CodeSystem/longevity360-field';

export type CareModelInputEventType =
  | 'metabolic.plan_proposed'
  | 'functional.plan_proposed'
  | 'lifestyle.plan_proposed'
  | 'functional.clearance_recorded'
  | 'cpet.review_recorded';

const PLAN_CODES: Record<string, CareModelInputEventType> = {
  'T12-M5': 'metabolic.plan_proposed',
  'T12-F5': 'functional.plan_proposed',
  'T12-P5': 'lifestyle.plan_proposed',
};
const CLEARANCE_CODE = 'CLEARANCE-F1';
const CPET_REVIEW_CODE = 'CPET-F2';
const PLAN_FIELDS = ['indication', 'scope', 'owner_role', 'start', 'review', 'safety_limits'] as const;
const CLEARANCE_TESTS = ['cpet', 'body_composition', 'cognition'] as const;
const DECISIONS: Record<string, string> = { cleared: 'cleared', held: 'held', 'not-indicated': 'not_indicated', pending: 'pending' };


/**
 * The id the event is keyed on: the answer set id when the QR carries one, else the QR id. A save after a sign makes a NEW
 * QR row but keeps the answer set, so the same answers in a new row are the same idempotency key.
 */
export function answerSetRef(stored: QuestionnaireResponseResource | null | undefined): string | undefined {
  return answerSetIdOf(stored) ?? stored?.id;
}

export interface CareModelInputContext {
  /** The patient's single L360 care plan id; null or undefined when there is none or more than one. */
  carePlanId?: string | null;
  reviewerId?: string;
  /** The stored QuestionnaireResponse id: the row just saved. Only the CPET reportRef fallback uses it. */
  qrId: string;
}

export interface CareModelInputEvent {
  eventType: CareModelInputEventType;
  payload: Record<string, unknown>;
  revision: string;
}

function findRootCode(items: readonly FhirQuestionnaireItem[] | null | undefined): { code: string; root: FhirQuestionnaireItem } | null {
  for (const it of items ?? []) {
    const code = it.type === 'group' ? it.code?.find((c) => c.system === L360_TEMPLATE_SYSTEM)?.code : undefined;
    if (code && (code in PLAN_CODES || code === CLEARANCE_CODE || code === CPET_REVIEW_CODE)) return { code, root: it };
    const inner = findRootCode(it.item);
    if (inner) return inner;
  }
  return null;
}

/** The event type the questionnaire drives, or null when it is not one of the five input templates. */
export function careModelInputTemplateEvent(questionnaire: FhirQuestionnaire | null | undefined): CareModelInputEventType | null {
  const found = findRootCode(questionnaire?.item);
  if (!found) return null;
  if (found.code === CLEARANCE_CODE) return 'functional.clearance_recorded';
  if (found.code === CPET_REVIEW_CODE) return 'cpet.review_recorded';
  return PLAN_CODES[found.code] ?? null;
}

/** Reads answers by field id for one template root. */
function fieldReader(code: string, root: FhirQuestionnaireItem, storedQr: QuestionnaireResponseResource | null | undefined) {
  const answers = indexResponse(storedQr?.item, new Map());
  const linkByField = new Map<string, string>();
  for (const it of collectItems(root.item, [])) {
    const field = it.code?.find((c) => c.system === FIELD_SYSTEM)?.code;
    if (field?.startsWith(`${code}.`)) linkByField.set(field.slice(code.length + 1), it.linkId);
  }
  const all = (id: string): QRAnswerWire[] => (answers.get(linkByField.get(id) ?? '') ?? []).filter(hasValue);
  return {
    answered: (id: string) => all(id).length > 0,
    /** Text of the first answer: a string, a date or the option slug. Empty when open. */
    text: (id: string): string => {
      const a = all(id)[0];
      if (!a) return '';
      if (a.valueCoding?.code) return a.valueCoding.code.slice(a.valueCoding.code.lastIndexOf('.') + 1);
      return (a.valueString ?? a.valueDateTime ?? '').trim();
    },
  };
}

/** A FHIR-style reference (`DocumentReference/rep-2210`) or an http(s) link, with no spaces. */
export const isReportReference = (text: string): boolean =>
  /^[A-Z][A-Za-z]+\/[A-Za-z0-9\-.]{1,64}$/.test(text) || /^https?:\/\/\S+$/.test(text);

function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * True when the questionnaire is a plan proposal (T12-M5, T12-F5, T12-P5) and all six required fields are answered. The sender
 * loads the care plans only then, so the fetch never runs on each autosave of a half-filled proposal.
 */
export function isPlanProposalComplete(questionnaire: FhirQuestionnaire | null | undefined, storedQr: QuestionnaireResponseResource | null | undefined): boolean {
  const found = findRootCode(questionnaire?.item);
  if (!found || !(found.code in PLAN_CODES)) return false;
  const f = fieldReader(found.code, found.root, storedQr);
  return PLAN_FIELDS.every((id) => f.answered(id));
}

/** The event to post for a saved input template, or null (see the header). Free text never enters the revision. */
export function careModelInputEvent(
  questionnaire: FhirQuestionnaire | null | undefined,
  storedQr: QuestionnaireResponseResource | null | undefined,
  ctx: CareModelInputContext,
): CareModelInputEvent | null {
  const found = findRootCode(questionnaire?.item);
  if (!found) return null;
  const f = fieldReader(found.code, found.root, storedQr);
  const reviewer = `reviewer=${ctx.reviewerId ?? ''}`;
  const withReviewer = ctx.reviewerId ? { reviewerId: ctx.reviewerId } : {};

  const planEvent = PLAN_CODES[found.code];
  if (planEvent) {
    if (!ctx.carePlanId || !PLAN_FIELDS.every((id) => f.answered(id))) return null;
    const state = PLAN_FIELDS.map((id) => `${id}=1`).join('|');
    // The plan id is in the revision: the same answers for another plan are another event, never a 409 on a reused key.
    const text = `${reviewer}|plan=${ctx.carePlanId}|${state}|owner_role=${f.text('owner_role')}|start=${f.text('start')}|review=${f.text('review')}`;
    return { eventType: planEvent, payload: { carePlanId: ctx.carePlanId, ...withReviewer }, revision: fnv1a(text) };
  }

  if (found.code === CLEARANCE_CODE) {
    const decisions = CLEARANCE_TESTS.map((test) => ({ test, decision: DECISIONS[f.text(test)] }));
    if (decisions.some((d) => !d.decision)) return null;
    const cpet = f.text('cpet');
    if (cpet === 'cleared' && !f.answered('cpet_protocol')) return null;
    if (cpet === 'held' && !f.answered('cpet_hold_reason')) return null;
    const text = `${reviewer}|${decisions.map((d) => `${d.test}=${d.decision}`).join('|')}|protocol=${f.text('cpet_protocol')}|hold=${f.answered('cpet_hold_reason') ? 1 : 0}`;
    return { eventType: 'functional.clearance_recorded', payload: { decisions, ...withReviewer }, revision: fnv1a(text) };
  }

  // CPET-F2: everything but `report` is required.
  const required = ['test_date', 'vo2_max', 'effort_valid', 'completed_safely', 'protocol', 'report_type'];
  if (!required.every((id) => f.answered(id))) return null;
  if (f.text('report_type') !== 'actual-report') return null; // illustrative (or an unknown type) never counts
  // Only a reference (`Type/id`) or a link goes out as reportRef. Other text ("see PDF") is not a reference: use the saved row.
  const report = f.text('report');
  const reportRef = isReportReference(report) ? report : `QuestionnaireResponse/${ctx.qrId}`;
  const effort = f.text('effort_valid');
  const safely = f.text('completed_safely');
  const text = [reviewer, `test_date=${f.text('test_date')}`, `effort_valid=${effort}`, `completed_safely=${safely}`, `protocol=${f.text('protocol')}`, 'report_type=actual-report', 'vo2_max=1', `report=${f.text('report')}`].join('|');
  return {
    eventType: 'cpet.review_recorded',
    payload: { reportRef, effortReviewed: effort === 'yes', safelyCompleted: safely === 'yes', ...withReviewer },
    revision: fnv1a(text),
  };
}
